import axios, { AxiosResponse } from 'axios';
import moment from "moment";
import { localize } from "src/l10n";
import api from "src/spintr/SpintrApi";
import { SpintrTypes } from "src/typings";
import { IAcademyChapter, IAcademyCourse, IAcademyQuestion, IAcademyTest, ICourseStep } from "./types";

const isChapterDone = (chapter: IAcademyChapter) : boolean => {
    return chapter.lessons.every(x => x.userProgress.isCompleted) &&
        (!chapter.enableTest || chapter.test.userProgress.isCompleted);
}

const getSteps = (course: IAcademyCourse): ICourseStep[] => {
    const steps: ICourseStep[] = [];

    for (const chapter of course.chapters) {
        for (const lesson of chapter.lessons) {
            steps.push({
                id: lesson.id,
                isCompleted: lesson.userProgress.isCompleted,
                isEligable: lesson.userProgress.isEligable,
                type: "lesson"
            });
        }

        if (chapter.enableTest) {
            steps.push({
                id: chapter.test.id,
                isCompleted: chapter.test.userProgress.isCompleted,
                isEligable: chapter.test.userProgress.isEligable,
                type: "test"
            });
        }
    }

    if (course.enableFinalExam) {
        steps.push({
            id: course.test.id,
            isCompleted: course.test.userProgress.isCompleted,
            isEligable: course.test.userProgress.isEligable,
            type: "test"
        });
    }
    
    return steps;
}

const isEligableToStep = (steps: ICourseStep[], id: number): boolean => {
    for (const step of steps) {
        if (step.id === id) {
            return true;
        } else if (!step.isCompleted) {
            return false;
        }
    }

    return false;
}

const getChapterProgressPercent = (chapter: IAcademyChapter) : number => {
    const steps = [
        true,
        ...(chapter.lessons.map(x => x.userProgress.isCompleted)),
        ...(chapter.enableTest ? [chapter.test.userProgress.isCompleted] : [])
    ];

    return (steps.filter(x => x).length / steps.length) * 100;
}

const getCourseProgressPercent = (course: IAcademyCourse) : number => {
    const progress = [];

    course.chapters.forEach(c => {
        c.lessons.forEach(l => {
            progress.push(l.userProgress.isCompleted);
        });

        if (c.enableTest) {
            progress.push(c.test.userProgress.isCompleted);
        }
    });

    if (course.enableFinalExam) {
        progress.push(course.test.userProgress.isCompleted);
    }

    return progress.length > 0 ? course.userProgress.completionPercent = Math.round(
        (progress.filter(x => x).length / progress.length) * 100
    ) : 0;
}

export const markStepAsCompleted = (course: IAcademyCourse, id: number) : IAcademyCourse => {
    for (const chapter of course.chapters) {
        for (const lesson of chapter.lessons) {
            if (lesson.id === id) {
                lesson.userProgress.isCompleted = true;
                chapter.userProgress.isCompleted = isChapterDone(chapter);
            }
        }

        if (chapter.enableTest && chapter.test.id === id) {
            chapter.test.userProgress.isCompleted = true;
            chapter.userProgress.isCompleted = true;
        }
    }

    if (course.enableFinalExam && course.test.id === id) {
        course.test.userProgress.isCompleted = true;
        course.userProgress.isCompleted = true;
    }

    return course;
}

export const getPreviousStep = (course: IAcademyCourse, currentStepId: number): ICourseStep => {
    const steps = getSteps(course);

    for (let i = 0; i < steps.length; i++) {
        if (i !== 0 && steps[i].id === currentStepId) {
            return steps[i - 1];
        }
    }

    return undefined;
}

export const getNextStep = (course: IAcademyCourse, previousStepId: number) : ICourseStep => {
    const steps = getSteps(course);

    for (let i = 0; i < steps.length; i++) {
        if (i !== 0 && steps[i - 1].id === previousStepId) {
            return steps[i];
        }
    }

    return undefined;
}

export const getActiveStep = (course: IAcademyCourse, preferredLessonId?: number) : ICourseStep => {
    const steps = getSteps(course);

    if (preferredLessonId) {
        const preferred = steps.find((step) =>
            step.type === "lesson" &&
            step.id === preferredLessonId &&
            step.isEligable);

        if (preferred) {
            return preferred;
        }
    }

    for (const step of steps) {
        if (!step.isCompleted &&
            step.isEligable) {
            return step;
        }
    }

    return getSteps(course)[0];
}

export const updateCourseEligibality = (course: IAcademyCourse, firstTime?: boolean) : IAcademyCourse => {
    if (course.isScorm) {
        // SCORM courses have no Spintr steps (chapters/lessons/tests). Completion
        // and progress come from the server (AcademyCourseUser, mirrored from the
        // package's reported status). Skip the step-based calc below, which would
        // treat "no steps" as fully complete (steps.every(...) is true for []).
        return course;
    }

    if (course.isGuide && firstTime) {
        for (const chapter of course.chapters) {
            for (const lesson of chapter.lessons) {
                lesson.userProgress.isEligable = true;
                lesson.userProgress.isCompleted = false;
            }

            chapter.userProgress = {
                isCompleted: false,
                isEligable: true,
                completionPercent: 0,
            };
        }

        return course;
    }

    const steps = getSteps(course);

    for (const chapter of course.chapters) {
        for (const lesson of chapter.lessons) {
            lesson.userProgress.isEligable = course.isGuide ? true : isEligableToStep(steps, lesson.id);
        }

        if (chapter.enableTest) {
            chapter.test.userProgress.isEligable = isEligableToStep(steps, chapter.test.id);
        }
        
        chapter.userProgress = {
            isCompleted: isChapterDone(chapter),
            isEligable: chapter.lessons.length > 0 && chapter.lessons[0].userProgress.isEligable,
            completionPercent: getChapterProgressPercent(chapter)
        }
    }

    if (course.enableFinalExam) {
        course.test.userProgress.isEligable = isEligableToStep(steps, course.test.id);
    }

    course.userProgress.isCompleted = steps.every(x => x.isCompleted);
    course.userProgress.completionPercent = getCourseProgressPercent(course);
    course.userProgress.hasStarted = course.userProgress.isCompleted || course.userProgress.completionPercent > 0;

    return course;
}

export const saveTestAnswer = (test: IAcademyTest) : Promise<IAcademyTest> => {
    return new Promise((resolve, reject) => {
        api.post("/api/v1/academy/tests", test).then((response: AxiosResponse) => {
            const validationRequests = [];

            for (const question of response.data.questions.filter((x: IAcademyQuestion) => x.answer && !x.answer.isValidated)) {
                validationRequests.push(api.post("/api/v1/academy/tests/" + test.id + "/answers/" + question.answer.id + "/validate"));
            }

            Promise.all(validationRequests).then(() => {
                api.post("/api/v1/academy/tests/" + test.id + "/verify").then((verifyResult: AxiosResponse) => {
                    resolve(verifyResult.data);
                }).catch(reject);
            }).catch(reject);
        }).catch(reject);
    });
}

export const defaultTestSettings = {
    passThreshold: 100,
    lockoutEnabled: false,
    lockoutMinutes: 480,
    shuffleQuestions: false,
};

export const getRequiredCorrectCount = (questionCount: number, passThreshold?: number): number => {
    const threshold = passThreshold >= 1 && passThreshold <= 100 ? passThreshold : 100;

    return Math.floor((questionCount * threshold + 99) / 100);
}

export const answerPairs: { key: number; tags: string[] }[] = [
    { key: 1, tags: ["ACADEMY_TRUE", "ACADEMY_FALSE"] },
    { key: 2, tags: ["ACADEMY_RIGHT", "ACADEMY_WRONG"] },
    { key: 3, tags: ["Ja", "Nej"] },
    { key: 4, tags: ["ACADEMY_ACCURATE", "ACADEMY_INACCURATE"] },
];

export const getAnswerPairTexts = (answerPair?: number): string[] => {
    const pair = answerPairs.find((p) => p.key === answerPair) || answerPairs[0];

    return pair.tags.map((tag) => localize(tag));
}

export const isChoiceQuestion = (question: IAcademyQuestion): boolean => {
    return question.type === SpintrTypes.QuestionType.SimpleAnswer ||
        question.type === SpintrTypes.QuestionType.MultipleAnswer;
}

export const isMissingCorrectAnswer = (question: IAcademyQuestion): boolean => {
    return isChoiceQuestion(question) &&
        !(question.alternatives || []).some((a) => a.isCorrect);
}

export const isAnswerCorrect = (question: IAcademyQuestion, selectedAlternativeIds: number[]): boolean => {
    const correctIds = (question.alternatives || []).filter((a) => a.isCorrect).map((a) => a.id);
    const selectedIds = Array.from(new Set(selectedAlternativeIds));

    if (correctIds.length === 0) {
        return false;
    }

    if (question.type === SpintrTypes.QuestionType.MultipleAnswer) {
        return selectedIds.length === correctIds.length &&
            selectedIds.every((id) => correctIds.includes(id));
    }

    return selectedIds.length === 1 && correctIds.includes(selectedIds[0]);
}

export const isQuestionAnswered = (question: IAcademyQuestion): boolean => {
    return !!question.answer &&
        (!!question.answer.text || question.answer.alternatives?.length > 0);
}

export const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    const hoursText = hours === 1 ?
        localize("ACADEMY_DURATION_ONE_HOUR") :
        localize("ACADEMY_DURATION_HOURS").replace("{0}", hours.toString());

    const minutesText = remainingMinutes === 1 ?
        localize("ACADEMY_DURATION_ONE_MINUTE") :
        localize("ACADEMY_DURATION_MINUTES").replace("{0}", remainingMinutes.toString());

    if (hours === 0) {
        return minutesText;
    }

    if (remainingMinutes === 0) {
        return hoursText;
    }

    return localize("ACADEMY_DURATION_HOURS_AND_MINUTES")
        .replace("{0}", hoursText)
        .replace("{1}", minutesText);
}

export const formatRetryTime = (date: Date | string): string => {
    const retryTime = moment(date);

    return retryTime.isSame(moment(), "day") ?
        retryTime.format("LT") :
        retryTime.format("LLL");
}

export const shuffleQuestions = (questions: IAcademyQuestion[]): IAcademyQuestion[] => {
    const shuffled = [...questions];

    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled;
}

export const isFirstStep = (course: IAcademyCourse, id: number) => {
    const steps = getSteps(course);

    return steps[0].id === id;
}

export const isLastStep = (course: IAcademyCourse, id: number) => {
    const steps = getSteps(course);

    return steps[steps.length - 1].id === id;
}